import { TOKENS } from '@monthio/design-system/tokens'
import React from 'react'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'

import type {
	Asset,
	AssetType,
	RealEstate,
	RealEstateUnion,
	Vehicle,
	VehicleUnion,
} from '../../AssetOverview.types'

import {
	overarchingTypes,
	translatePropellant,
	VehiclePropellant,
} from '../../AssetOverview.types'
import { ButtonSelect } from '../ButtonSelect'
import {
	LabelledField,
	LabelledNumberField,
	LabelledTextField,
} from '../LabelledField'
import { SelectYearlyMileage } from './AddAsset'
import { H3 } from '@monthio/design-system'

export function nameof<T>(t: keyof T): keyof T {
	return t
}

export type Setter<T> = (
	value: ((asset: Partial<T>) => Partial<T>) | Partial<T>,
) => void

export const Background = styled.div`
	border-radius: var(--borderRadius-borderRadius-xs, 4px);
	background: var(--ColorTokens-Shapes-primary-surface, #fff);
	padding: var(--spacingSystem-space-s, 12px);
	display: flex;
	flex-direction: column;
	gap: ${TOKENS.space.xs};
`

export const AssetTypeSpecific: React.FC<{
	asset: Partial<Asset> & { assetType: AssetType }
	setAsset: Setter<Asset>
	formErrors?: Record<string, string>
}> = ({ asset, setAsset, formErrors }) => {
	switch (true) {
		case overarchingTypes.realEstate.includes(
			asset.assetType as RealEstateUnion,
		):
			return (
				<RealEstateFields
					estate={asset as RealEstate}
					setEstate={setAsset as Setter<RealEstate>}
					formErrors={formErrors}
				/>
			)

		case overarchingTypes.vehicle.includes(asset.assetType as VehicleUnion):
			return (
				<VehicleFields
					vehicle={asset as Vehicle}
					setVehicle={setAsset as Setter<Vehicle>}
					formErrors={formErrors}
				/>
			)
	}
	return <></>
}

export const FieldWrap = styled.div<{ $spanFirst?: boolean }>`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 8px 16px;

	@media screen and (max-width: 800px) {
		grid-template-columns: 1fr;
	}

	${(p) =>
		p.$spanFirst &&
		css`
			:first-child {
				@media screen and (min-width: 801px) {
					grid-column: span 2;
				}
			}
		`}
`

const PropellantSection = styled.div`
	margin: 16px 0;
`

const RealEstateFields: React.FC<{
	estate: RealEstate
	setEstate: Setter<RealEstate>
	formErrors?: Record<string, string>
}> = ({ estate, setEstate, formErrors }) => {
	const { t } = useTranslation()
	return (
		<>
			<H3>{t('asset-input-address')}</H3>
			<FieldWrap $spanFirst>
				<LabelledTextField
					placeholder="Bøgevej 20 - Nordenskov, 6800 Varde"
					name={nameof<RealEstate>('address')}
					label={t('asset-input-address-address')}
					value={estate.address}
					onChange={(address) => setEstate((e) => ({ ...e, address }))}
					formErrors={formErrors}
				/>
			</FieldWrap>
		</>
	)
}

const VehicleFields: React.FC<{
	vehicle: Vehicle
	setVehicle: Setter<Vehicle>
	formErrors?: Record<string, string>
}> = ({ vehicle, setVehicle, formErrors }) => {
	const { t } = useTranslation()
	return (
		<>
			<H3>{t('asset-input-vehicle-information')}</H3>
			<PropellantSection>
				{t('asset-input-propellant')}
				<ButtonSelect
					values={Object.values(VehiclePropellant).map((value) => ({
						value,
						displayValue: translatePropellant(value),
					}))}
					currentValue={vehicle.propellant}
					onChange={(propellant) => {
						setVehicle((e) => ({
							...e,
							propellant,
						}))
					}}
				/>
			</PropellantSection>
			<FieldWrap>
				<LabelledTextField
					name={nameof<Vehicle>('brand')}
					label={t('asset-input-brand')}
					placeholder="Ford"
					formErrors={formErrors}
					value={vehicle.brand}
					onChange={(brand) => setVehicle((e) => ({ ...e, brand }))}
				/>
				<LabelledTextField
					name={nameof<Vehicle>('model')}
					label={t('asset-input-model')}
					placeholder="Focus"
					formErrors={formErrors}
					value={vehicle.model}
					onChange={(model) => setVehicle((e) => ({ ...e, model }))}
				/>
				<LabelledTextField
					name={nameof<Vehicle>('variant')}
					label={t('asset-input-variant')}
					placeholder="St line"
					formErrors={formErrors}
					value={vehicle.variant}
					onChange={(variant) => setVehicle((e) => ({ ...e, variant }))}
				/>
				<LabelledNumberField
					name={nameof<Vehicle>('year')}
					label={t('asset-input-model-year')}
					placeholder={2020}
					formErrors={formErrors}
					value={vehicle.year}
					onChange={(year) => setVehicle((e) => ({ ...e, year }))}
				/>
				<LabelledField
					name={nameof<Vehicle>('yearlyMileage')}
					label={t('asset-input-yearlyMileage')}
					formErrors={formErrors}
				>
					<SelectYearlyMileage
						asset={vehicle}
						onChange={(yearlyMileage) =>
							setVehicle((e) => ({ ...e, yearlyMileage }))
						}
					/>
				</LabelledField>
			</FieldWrap>
		</>
	)
}
